import React from "react";
import Modal from "react-modal";
import PropTypes from "prop-types";

import GenericConfirmModal from "./GenericConfirmModal";

Modal.setAppElement(document.body);

const bootstrapModalPropTypes = {
    // These are from the <Modal> Component
    isOpen: PropTypes.bool.isRequired,
    style: PropTypes.shape({
        content: PropTypes.object,
        overlay: PropTypes.object,
    }),
    portalClassName: PropTypes.string,
    onAfterOpen: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired,
    closeTimeoutMS: PropTypes.number,
    ariaHideApp: PropTypes.bool,
    shouldCloseOnOverlayClick: PropTypes.bool,

    // Specific to us, <BootstrapModal>, made by experian
    onRequestPrimary: PropTypes.func, // What happens if you click the primary button
    primaryLink: PropTypes.string, // What happens if you click the primary button, href, will override onRequestPrimary
    primaryButtonText: PropTypes.string, // Text on primary button, defaults to "OK"
    primaryButtonClassName: PropTypes.string, // ClassName of primary button, defaults to
    closeButtonText: PropTypes.string, // Text on close button, defaults to "Close"
    isPrimaryButtonDisabled: PropTypes.bool, // Should the primary button be disabled?
    showPrimaryButton: PropTypes.bool, // Should the primary button be shown?
    showConfirmModal: PropTypes.bool, // Should the primary button be shown?
    confirmTitle: PropTypes.string,
    confirmButtonText: PropTypes.string,
    confirmBody: PropTypes.node,
    isLarge: PropTypes.bool, // Make modal wider
    title: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.node, // Optional override of entire footer element, for more complicated setups
    classNameExtra: PropTypes.string, // Extra classnames to put on the modal-dialog class
};

type BootstrapModalProps = PropTypes.InferProps<typeof bootstrapModalPropTypes>;

const BootstrapModal: React.FC<BootstrapModalProps> = ({
    showPrimaryButton,
    showConfirmModal,
    confirmTitle,
    confirmButtonText,
    confirmBody,
    primaryButtonText,
    primaryButtonClassName,
    closeButtonText,
    onRequestClose,
    onRequestPrimary,
    primaryLink,
    title,
    children,
    isLarge,
    footer,
    isPrimaryButtonDisabled,
    isOpen,
    classNameExtra,
}: BootstrapModalProps) => {
    let primaryButton = (
        <button
            type="button"
            className={primaryButtonClassName || "btn btn-primary"}
            onClick={() => (onRequestPrimary ? onRequestPrimary() : null)}
            disabled={Boolean(isPrimaryButtonDisabled)}
        >
            {primaryButtonText || "OK"}
        </button>
    );

    if (primaryLink) {
        primaryButton = (
            <a className="btn btn-primary" href={primaryLink}>
                {primaryButtonText || "OK"}
            </a>
        );
    }

    if (showConfirmModal) {
        primaryButton = (
            <GenericConfirmModal
                onConfirm={() => (onRequestPrimary ? onRequestPrimary() : null)}
                body={confirmBody || "Please confirm."}
                confirmText={confirmButtonText || "Confirm"}
                title={confirmTitle || "Confirm Action"}
                buttonText={primaryButtonText || "OK"}
                cnBtnStyle={primaryButtonClassName || "btn btn-primary"}
                buttonDisabled={Boolean(isPrimaryButtonDisabled)}
            />
        );
    }

    const defaultFooter = (
        <div>
            {showPrimaryButton ? primaryButton : null}
            <button
                type="button"
                className={"btn " + (showPrimaryButton ? "btn-default" : "btn-primary")}
                onClick={() => {
                    onRequestClose();
                }}
            >
                {closeButtonText || "Close"}
            </button>
        </div>
    );

    let extraDialogClasses = isLarge ? " modal-lg" : "";
    extraDialogClasses += " " + (classNameExtra || "");
    return isOpen ? (
        <Modal
            className={"Modal__Bootstrap modal-dialog " + extraDialogClasses}
            closeTimeoutMS={200}
            contentLabel={title}
            showPrimaryButton={showPrimaryButton}
            showConfirmModal={showConfirmModal}
            onRequestClose={onRequestClose}
            onRequestPrimary={onRequestPrimary}
            isOpen={isOpen}
        >
            <div className="modal-content">
                <div className="modal-header">
                    <button type="button" className="close" onClick={() => onRequestClose()}>
                        <span aria-hidden="true">&times;</span>
                        <span className="sr-only">Close</span>
                    </button>
                    <h4 className="modal-title">{title}</h4>
                </div>
                <div className="modal-body">
                    <div className="col-sm-12">{isOpen ? children : null}</div>
                    <div className="clearfix" />
                </div>
                <div className="modal-footer">{footer || defaultFooter}</div>
            </div>
        </Modal>
    ) : null;
};

BootstrapModal.defaultProps = { showPrimaryButton: true };

export default BootstrapModal;
